import { ActionTree } from "vuex";
import { RootState } from "@/store";
import { PendingCommentEdit, PendingNewReply, PendingReplyEdit } from "@/types/document/pendingDocumentComments";
import { namespace as pendingDocumentCommentsNamespace } from "../modules/document/pendingDocumentComments";
import { Store } from "vuex";

export class PendingDocumentCommentStore {
    private store: Store<any>;

    constructor(store: Store<any>) {
        this.store = store;
    }

    /** Gets the pending comment edits  */
    public get commentEdits(): PendingCommentEdit[] {
        return this.store.getters[`${pendingDocumentCommentsNamespace}/commentEdits`];
    }

    /** Gets the pending comment edits  */
    public get newReplies(): PendingNewReply[] {
        return this.store.getters[`${pendingDocumentCommentsNamespace}/newReplies`];
    }

    /** Gets the pending comment edits  */
    public get replyEdits(): PendingReplyEdit {
        return this.store.getters[`${pendingDocumentCommentsNamespace}/replyEdits`];
    }

    /**
     * Adds or updates a pending comment edit
     * @param pendingCommentEdit The comment data of the pending comment edit
     */
    public addUpdateCommentEdit(pendingCommentEdit: PendingCommentEdit): void {
        this.store.dispatch(`${pendingDocumentCommentsNamespace}/addUpdateCommentEdit`, pendingCommentEdit);
    }

    /**
     * Removes a pending comment edit
     * @param commentID The id of the comment to remove from the pending comment edits
     */
    public removeCommentEdit(commentID: number): void {
        this.store.dispatch(`${pendingDocumentCommentsNamespace}/removeCommentEdit`, commentID);
    }

    /**
     * Adds or updates a pending new reply
     * @param pendingNewReply The reply data of the pending new reply
     */
    public addUpdateNewReplies(pendingNewReply: PendingNewReply): void {
        this.store.dispatch(`${pendingDocumentCommentsNamespace}/addUpdateNewReplies`, pendingNewReply);
    }

    /**
     * Removes a pending new reply
     * @param commentID The commentID of the pending new reply to remove. A comment can have only one pending new reply
     */
    public removeNewReplies(commentID: number): void {
        this.store.dispatch(`${pendingDocumentCommentsNamespace}/removeNewReplies`, commentID);
    }

    /**
     * Adds or updates a pending reply edit
     * @param pendingReplyEdit The reply data of the pending reply edit
     */
    public addUpdateReplyEdits(pendingReplyEdit: PendingReplyEdit): void {
        this.store.dispatch(`${pendingDocumentCommentsNamespace}/addUpdateReplyEdits`, pendingReplyEdit);
    }

    /**
     * Removes a pending reply edit
     * @param replyID The id of the reply to remove from the pending reply edits
     */
    public removeReplyEdits(replyID: number): void {
        this.store.dispatch(`${pendingDocumentCommentsNamespace}/removeReplyEdits`, replyID);
    }

    /**
     * Removes a pending reply edit
     * @param commentID The id of the comment for which to remove all pending reply edits
     */
    public removeReplyEditsForComment(commentID: number): void {
        this.store.dispatch(`${pendingDocumentCommentsNamespace}/removeReplyEditsForComment`, commentID);
    }

    /**
     * Returns wether for a document there are pending changes regarding comments
     * @param documentID The id of the document for which to determine whether there are changes
     */
    public hasPendingItems(documentID: string): boolean {
        return this.store.getters[`${pendingDocumentCommentsNamespace}/hasPendingItems`](documentID);
    }

    /**
     * Saves pending changes
     * @param replyID The id of document for which to save the pending changes
     */
    public savePendingItems(documentID: string): void {
        this.store.dispatch(`${pendingDocumentCommentsNamespace}/savePendingItems`, documentID);
    }
}
